import { Task } from "../../pojo/Task";
import { TaskChange, TaskService } from "../../services/task/task.service";
import { TaskUtil } from "./task.util";
import { S25Util } from "../../util/s25-util";
import { ManageWaitlistTaskInfo } from "./manage.tasks.component";
import { Item } from "../../pojo/Item";

/**
 * Centralized utility for handling competing/conflicting tasks in waitlist scenarios.
 * Provides a consistent interface for checking, confirming, and denying competing tasks.
 */
export namespace TaskCompetingUtil {
    /**
     * Result of analyzing competing tasks
     */
    export interface CompetingTasksInfo {
        hasCompetingTasks: boolean; // whether there are any competing tasks
        count: number; // number of competing tasks/events
        tasksToNotify: TaskChange[]; // tasks to be denied
        competingEventIds: number[]; // unique event IDs that have competing tasks
    }

    /**
     * Analyzes a single task's competes data to determine what tasks would be affected
     * @param competes - Array of space compete data
     * @returns Information about competing tasks
     */
    export async function analyzeCompetes(competes?: Task.SpaceCompete[]): Promise<CompetingTasksInfo> {
        if (!competes || competes.length === 0) {
            return {
                hasCompetingTasks: false,
                count: 0,
                tasksToNotify: [],
                competingEventIds: [],
            };
        }

        const eventIds = Array.from(new Set(competes.map((c) => c.conflictId)));
        const tasksToNotify = await TaskUtil.deniedCompeteTasks(eventIds, competes);

        return {
            hasCompetingTasks: tasksToNotify.length > 0,
            count: tasksToNotify.length,
            tasksToNotify,
            competingEventIds: eventIds,
        };
    }

    /**
     * Analyzes multiple tasks to find competing tasks
     * Used in batch operations like "Approve Checked"
     * @param tasks - Array of tasks being approved
     * @returns Information about all competing tasks
     */
    export function analyzeMultipleCompetes(tasks: ManageWaitlistTaskInfo[]): {
        allCompetes: Task.SpaceCompete[];
        competingEventIds: number[];
        count: number;
    } {
        const allCompetes = tasks.flatMap((task) => task.competes || []);
        const competingEventIds = Array.from(new Set(allCompetes.map((c) => c.conflictId)));

        return {
            allCompetes,
            competingEventIds,
            count: competingEventIds.length,
        };
    }

    /**
     * Builds a list of TaskChange objects for tasks that should be denied based on competes data
     * @param taskItems - All task items to check
     * @param competes - Space compete data
     * @returns Array of TaskChange objects for matching tasks
     */
    export function buildDeniedTaskChanges(
        taskItems: ManageWaitlistTaskInfo[],
        competes: Task.SpaceCompete[],
    ): TaskChange[] {
        return competes.reduce<TaskChange[]>((acc, compete) => {
            const matchItem = taskItems.find(
                (taskItem) =>
                    taskItem.item.itemId === compete.spaceId &&
                    taskItem.objectType === Item.Ids.Location &&
                    taskItem.reservationId === compete.conflictRsrvId,
            );

            if (matchItem) {
                acc.push({
                    eventId: compete.conflictId,
                    itemId: matchItem.id,
                    itemTypeId: matchItem.item?.itemTypeId,
                    objectType: matchItem.objectType,
                    objectId: matchItem.item?.itemId,
                    itemStateId: matchItem.stateId,
                    isTodo: false,
                });
            }

            return acc;
        }, []);
    }

    /**
     * Approves tasks with optional denial of competing tasks
     * @param tasksToApprove - Tasks to approve
     * @param competingInfo - Information about competing tasks (optional)
     * @param shouldDenyCompeting - Whether to also deny competing tasks (default: false)
     * @returns Result of the approval operation
     */
    export async function approveWithOptionalDenial(
        tasksToApprove: TaskChange[],
        competingInfo?: CompetingTasksInfo,
        shouldDenyCompeting: boolean = false,
    ): Promise<{ success: boolean; error?: any }> {
        // First, approve the selected tasks
        const [approveResp, approveErr] = await S25Util.Maybe(
            TaskService.changeTask(tasksToApprove, Task.States.Assigned),
        );

        if (approveErr) {
            return { success: false, error: approveErr };
        }

        // Optionally deny competing tasks if requested and they exist
        if (shouldDenyCompeting && competingInfo?.hasCompetingTasks && competingInfo.tasksToNotify.length > 0) {
            const [denyResp, denyErr] = await S25Util.Maybe(
                TaskService.changeTask(competingInfo.tasksToNotify, Task.States.Denied),
            );

            if (denyErr) {
                // Approval succeeded but denial failed - log but don't fail the whole operation
                console.warn("Tasks approved but failed to deny competing tasks:", denyErr);
            }
        }

        return { success: true };
    }

    /**
     * Checks for overlapping waitlist items within a set of tasks being approved
     * Returns task IDs that have time conflicts with each other
     * @param tasks - Tasks being approved that are in waitlist
     * @returns Array of task IDs that overlap with each other
     */
    export function findWaitlistOverlaps(tasks: ManageWaitlistTaskInfo[]): number[] {
        const overlappingIds = new Set<number>();

        for (let i = 0; i < tasks.length; i++) {
            const itemA = tasks[i];
            if (!itemA.competes || itemA.competes.length === 0) continue;

            for (const competeA of itemA.competes) {
                const startA = S25Util.date.toS25ISODateTimeStr(competeA.conflictPreEventDt);
                const endA = S25Util.date.toS25ISODateTimeStr(competeA.conflictPostEventDt);
                if (!startA || !endA) continue;

                for (let j = i + 1; j < tasks.length; j++) {
                    const itemB = tasks[j];
                    if (!itemB.competes || itemB.competes.length === 0) continue;

                    for (const competeB of itemB.competes) {
                        const startB = S25Util.date.toS25ISODateTimeStr(competeB.conflictPreEventDt);
                        const endB = S25Util.date.toS25ISODateTimeStr(competeB.conflictPostEventDt);
                        if (!startB || !endB) continue;

                        const isOverlap = S25Util.date.doRangesOverlap(startA, endA, startB, endB);

                        if (isOverlap) {
                            overlappingIds.add(itemA.id);
                            overlappingIds.add(itemB.id);
                        }
                    }
                }
            }
        }

        return Array.from(overlappingIds);
    }

    /**
     * Generates a user-friendly message for the confirmation dialog
     * @param count - Number of competing tasks
     * @param actionType - Type of action being performed (e.g., "Assign", "Approve")
     * @returns Confirmation message string
     */
    export function getConfirmationMessage(count: number, actionType: string = "Assigning"): string {
        if (count === 0) return "";
        if (count === 1) return `${actionType} this location will deny 1 competing request. Continue?`;
        return `${actionType} this location will deny ${count} competing requests. Continue?`;
    }
}
